<template>
  <div class="dash">
    <header class="dash-hero">
      <div>
        <p class="eyebrow">{{ greeting }}{{ displayName ? `, ${displayName}` : '' }}</p>
        <h1>Field Flow overview</h1>
        <p class="hero-sub">
          {{ locationName || 'This location' }}
          <span v-if="companyName"> · {{ companyName }}</span>
        </p>
      </div>
      <div class="hero-actions">
        <span class="updated">{{ lastUpdatedLabel }}</span>
        <button
          type="button"
          class="ghost-btn"
          :disabled="loading"
          @click="refreshAll"
        >
          <RefreshCw :size="16" :class="{ spinning: loading }" />
          {{ loading ? 'Updating…' : 'Refresh' }}
        </button>
      </div>
    </header>

    <section class="kpi-grid">
      <article class="kpi" :class="connectionTone">
        <div class="kpi-icon"><Plug :size="20" /></div>
        <div class="kpi-body">
          <div class="kpi-label">ServiceM8</div>
          <div class="kpi-value">{{ connectionLabel }}</div>
          <div class="kpi-meta">{{ connectionMeta }}</div>
        </div>
      </article>

      <article class="kpi" :class="workflowTone">
        <div class="kpi-icon"><Workflow :size="20" /></div>
        <div class="kpi-body">
          <div class="kpi-label">Workflows</div>
          <div class="kpi-value">{{ workflowValue }}</div>
          <div class="kpi-meta">{{ workflowMeta }}</div>
        </div>
      </article>

      <article class="kpi">
        <div class="kpi-icon"><Users :size="20" /></div>
        <div class="kpi-body">
          <div class="kpi-label">Synced records</div>
          <div class="kpi-value">{{ mappedContacts + mappedJobs }}</div>
          <div class="kpi-meta">{{ mappedContacts }} contacts · {{ mappedJobs }} jobs</div>
        </div>
      </article>

      <article class="kpi" :class="reliabilityTone">
        <div class="kpi-icon"><ShieldCheck :size="20" /></div>
        <div class="kpi-body">
          <div class="kpi-label">Reliability</div>
          <div class="kpi-value">{{ successRateLabel }}</div>
          <div class="kpi-meta">{{ reliabilityMeta }}</div>
        </div>
      </article>
    </section>

    <section v-if="showSetup" class="card setup-card">
      <div class="card-head">
        <div>
          <h2>Get started</h2>
          <p>A few setup steps so Field Flow can keep ServiceM8 and {{ brand.agencyName }} in sync.</p>
        </div>
      </div>
      <ol class="setup-list">
        <li :class="{ done: connection.connected && connection.accountValid }">
          <span class="setup-mark">{{ connection.connected && connection.accountValid ? '✓' : '1' }}</span>
          <div>
            <strong>Connect ServiceM8</strong>
            <p>{{ connection.connected ? 'Connected. You can reconnect any time from Integration.' : 'Link your ServiceM8 account so jobs and clients can sync.' }}</p>
          </div>
          <button type="button" class="text-btn" @click="goTo('Integration', 'Connections')">
            {{ connection.connected ? 'Manage' : 'Connect' }}
          </button>
        </li>
        <li :class="{ done: mappedContacts > 0 }">
          <span class="setup-mark">{{ mappedContacts > 0 ? '✓' : '2' }}</span>
          <div>
            <strong>Sync contacts</strong>
            <p>{{ mappedContacts > 0 ? `${mappedContacts} contacts are already linked.` : 'Copy clients between ServiceM8 and your CRM so both stay up to date.' }}</p>
          </div>
          <button type="button" class="text-btn" @click="goTo('Integration', 'Manual Sync')">Sync now</button>
        </li>
        <li :class="{ done: (webhookStatus.triggerCount || 0) > 0 }">
          <span class="setup-mark">{{ (webhookStatus.triggerCount || 0) > 0 ? '✓' : '3' }}</span>
          <div>
            <strong>Turn on workflow updates</strong>
            <p>{{ (webhookStatus.triggerCount || 0) > 0 ? `${webhookStatus.triggerCount} workflows are listening for ServiceM8 changes.` : 'Add a Field Flow trigger in a workflow if you want automatic updates.' }}</p>
          </div>
          <button type="button" class="text-btn" @click="goTo('Troubleshooting')">Check health</button>
        </li>
      </ol>
    </section>

    <div class="dash-columns">
      <section class="card activity-card">
        <div class="card-head">
          <div>
            <h2>Recent activity</h2>
            <p>The latest syncs, workflow actions, and automatic checks.</p>
          </div>
          <button type="button" class="ghost-btn" @click="goTo('Troubleshooting')">
            See all
            <ArrowRight :size="16" />
          </button>
        </div>

        <div v-if="loading && !recentHistory.length" class="empty">Loading activity…</div>
        <div v-else-if="!recentHistory.length" class="empty">
          Nothing has happened yet. After you connect ServiceM8 and sync, activity will show up here.
        </div>
        <ul v-else class="activity-feed">
          <li v-for="item in recentHistory.slice(0, 8)" :key="item.id">
            <span class="dot" :class="item.status === 'success' ? 'ok' : 'bad'" />
            <div class="activity-main">
              <strong>{{ friendlyType(item.syncType) }}</strong>
              <span class="activity-note">{{ item.errorMessage || friendlyDirection(item.direction) }}</span>
            </div>
            <div class="activity-side">
              <span class="pill" :class="item.status === 'success' ? 'ok' : 'bad'">
                {{ item.status === 'success' ? 'Worked' : 'Problem' }}
              </span>
              <time>{{ relativeTime(item.syncedAt) }}</time>
            </div>
          </li>
        </ul>
      </section>

      <div class="side-stack">
        <section class="card">
          <div class="card-head">
            <div>
              <h2>Quick actions</h2>
              <p>Common tasks, without hunting through menus.</p>
            </div>
          </div>
          <div class="action-grid">
            <button type="button" class="action-tile" @click="goTo('Integration', 'Manual Sync')">
              <Users :size="18" />
              <span>Sync contacts</span>
            </button>
            <button type="button" class="action-tile" @click="goTo('Integration', 'Manual Sync')">
              <Briefcase :size="18" />
              <span>Sync jobs</span>
            </button>
            <button type="button" class="action-tile" @click="goTo('Integration', 'Connections')">
              <Plug :size="18" />
              <span>Connections</span>
            </button>
            <button type="button" class="action-tile" @click="goTo('Troubleshooting')">
              <Activity :size="18" />
              <span>Troubleshoot</span>
            </button>
          </div>
        </section>

        <section class="card">
          <div class="card-head">
            <div>
              <h2>This week</h2>
              <p>{{ weekTotal }} events in the last 7 days.</p>
            </div>
          </div>
          <ul class="bars">
            <li v-for="bar in weekBars" :key="bar.key">
              <div class="bar-label">
                <span>{{ bar.label }}</span>
                <span>{{ bar.count }}</span>
              </div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: bar.pct + '%' }" />
              </div>
            </li>
          </ul>
        </section>

        <section class="card account-card">
          <div class="card-head">
            <div>
              <h2>This account</h2>
              <p>Who is signed in and which location Field Flow is using.</p>
            </div>
          </div>
          <dl class="account-dl">
            <div>
              <dt>Signed in as</dt>
              <dd>{{ displayName || '—' }}</dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>{{ locationName || '—' }}</dd>
            </div>
            <div>
              <dt>Company</dt>
              <dd>{{ companyName || '—' }}</dd>
            </div>
          </dl>
        </section>
      </div>
    </div>

    <section class="card details-card">
      <div class="card-head">
        <div>
          <h2>Company details</h2>
          <p>Account information from {{ brand.agencyName }}.</p>
        </div>
      </div>
      <div v-if="!hasCompanyDetails" class="empty">Company details are not available yet.</div>
      <dl v-else class="details-grid">
        <div>
          <dt>Company name</dt>
          <dd>{{ companyName || '—' }}</dd>
        </div>
        <div>
          <dt>Company ID</dt>
          <dd class="mono">{{ companyId || '—' }}</dd>
        </div>
        <div v-if="companyEmail">
          <dt>Email</dt>
          <dd>{{ companyEmail }}</dd>
        </div>
        <div v-if="companyPhone">
          <dt>Phone</dt>
          <dd>{{ companyPhone }}</dd>
        </div>
        <div v-if="companyWebsite">
          <dt>Website</dt>
          <dd>{{ companyWebsite }}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>{{ locationName || '—' }}</dd>
        </div>
        <div>
          <dt>Location ID</dt>
          <dd class="mono">{{ locationId || '—' }}</dd>
        </div>
      </dl>
    </section>

    <section class="card keys-card">
      <div class="card-head">
        <div>
          <h2>API keys</h2>
          <p>For testing only. Do not share these tokens.</p>
        </div>
        <button
          type="button"
          class="ghost-btn"
          :disabled="loadingKeys"
          @click="loadApiKeys"
        >
          {{ loadingKeys ? 'Loading…' : 'Reload keys' }}
        </button>
      </div>
      <div v-if="loadingKeys && !apiKeys" class="empty">Loading API keys…</div>
      <div v-else-if="!apiKeys" class="empty">Could not load API keys.</div>
      <div v-else class="keys-list">
        <article class="key-row">
          <div>
            <strong>{{ brand.agencyName }}</strong>
            <p>{{ apiKeys.ghlApiDomain }}</p>
          </div>
          <code>{{ apiKeys.ghlAccessToken }}</code>
          <button type="button" class="ghost-btn" @click="copyToClipboard(apiKeys.ghlAccessToken, 'ghl')">
            {{ copiedKey === 'ghl' ? 'Copied' : 'Copy' }}
          </button>
        </article>
        <article class="key-row">
          <div>
            <strong>ServiceM8</strong>
            <p>{{ apiKeys.servicem8ApiDomain }}</p>
          </div>
          <code>{{ apiKeys.servicem8AccessToken }}</code>
          <button type="button" class="ghost-btn" @click="copyToClipboard(apiKeys.servicem8AccessToken, 'sm8')">
            {{ copiedKey === 'sm8' ? 'Copied' : 'Copy' }}
          </button>
        </article>
        <p class="key-note">Location ID: <code>{{ apiKeys.locationId }}</code></p>
      </div>
    </section>
  </div>
</template>

<script>
import {
  Activity,
  ArrowRight,
  Briefcase,
  Plug,
  RefreshCw,
  ShieldCheck,
  Users,
  Workflow,
} from 'lucide-vue-next';
import { brandMixin } from '../brandMixin';

export default {
  name: 'DashboardTab',
  mixins: [brandMixin],
  components: {
    Activity,
    ArrowRight,
    Briefcase,
    Plug,
    RefreshCw,
    ShieldCheck,
    Users,
    Workflow,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    userDetails: {
      type: Object,
      default: null,
    },
    companyDetails: {
      type: Object,
      default: null,
    },
    locationDetails: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      lastUpdated: null,
      recentHistory: [],
      mappedContacts: 0,
      mappedJobs: 0,
      connection: {
        connected: false,
        accountValid: true,
        accountName: '',
        validationError: '',
      },
      webhookStatus: {
        triggerCount: 0,
        healthyCount: 0,
        brokenCount: 0,
        connected: false,
        accountName: null,
        webhookLastCheckedAt: null,
      },
      apiKeys: null,
      loadingKeys: false,
      copiedKey: '',
    };
  },
  computed: {
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return 'Good morning';
      if (hour < 17) return 'Good afternoon';
      return 'Good evening';
    },
    displayName() {
      const details = this.userDetails || {};
      const raw =
        details.firstName ||
        details.first_name ||
        details.name ||
        this.user?.name ||
        this.user?.userName ||
        this.user?.email ||
        '';
      if (!raw) return '';
      return String(raw).split('@')[0].split(' ')[0];
    },
    companyName() {
      return this.companyDetails?.name || this.companyDetails?.companyName || '';
    },
    companyId() {
      return this.companyDetails?.id || this.companyDetails?.companyId || this.user?.companyId || '';
    },
    companyEmail() {
      return this.companyDetails?.email || '';
    },
    companyPhone() {
      return this.companyDetails?.phone || '';
    },
    companyWebsite() {
      return this.companyDetails?.website || '';
    },
    hasCompanyDetails() {
      return !!(this.companyName || this.companyId || this.locationName || this.locationId);
    },
    locationName() {
      return this.locationDetails?.location?.name || this.locationDetails?.name || '';
    },
    locationId() {
      return this.user?.locationId || this.user?.activeLocation || this.locationDetails?.location?.id || '';
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) return 'Not updated yet';
      return `Updated ${this.relativeTime(this.lastUpdated)}`;
    },
    connectionLabel() {
      if (!this.connection.connected) return 'Not connected';
      if (!this.connection.accountValid) return 'Needs reconnect';
      return 'Connected';
    },
    connectionMeta() {
      if (!this.connection.connected) return 'Open Connections to link ServiceM8';
      if (!this.connection.accountValid) return this.connection.validationError || 'Please reconnect ServiceM8';
      return this.connection.accountName || this.webhookStatus.accountName || 'Account is ready';
    },
    connectionTone() {
      if (!this.connection.connected) return 'tone-bad';
      if (!this.connection.accountValid) return 'tone-warn';
      return 'tone-ok';
    },
    workflowValue() {
      if (!this.connection.connected) return '—';
      return this.webhookStatus.triggerCount || 0;
    },
    workflowMeta() {
      if (!this.connection.connected) return 'Connect ServiceM8 first';
      const working = this.webhookStatus.healthyCount || 0;
      const broken = this.webhookStatus.brokenCount || 0;
      if (!this.webhookStatus.triggerCount) return 'No workflows listening yet';
      if (broken) return `${working} working · ${broken} need attention`;
      return `${working} working`;
    },
    workflowTone() {
      if (!this.connection.connected) return '';
      if ((this.webhookStatus.brokenCount || 0) > 0) return 'tone-warn';
      if ((this.webhookStatus.triggerCount || 0) > 0) return 'tone-ok';
      return '';
    },
    successCount() {
      return this.recentHistory.filter((item) => item.status === 'success').length;
    },
    failedCount() {
      return this.recentHistory.filter((item) => item.status === 'failed').length;
    },
    successRateLabel() {
      if (!this.recentHistory.length) return '—';
      return `${Math.round((this.successCount / this.recentHistory.length) * 100)}%`;
    },
    reliabilityMeta() {
      if (!this.recentHistory.length) return 'No activity yet';
      if (this.failedCount) return `${this.failedCount} problem${this.failedCount === 1 ? '' : 's'} in recent activity`;
      return 'Recent activity looks healthy';
    },
    reliabilityTone() {
      if (!this.recentHistory.length) return '';
      if (this.failedCount > 0) return 'tone-warn';
      return 'tone-ok';
    },
    showSetup() {
      return !this.connection.connected || !this.connection.accountValid || this.mappedContacts === 0;
    },
    weekItems() {
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return this.recentHistory.filter((item) => new Date(item.syncedAt).getTime() >= cutoff);
    },
    weekTotal() {
      return this.weekItems.length;
    },
    weekBars() {
      const groups = [
        { key: 'contacts', label: 'Contacts', match: (type) => type === 'contact_sync' },
        { key: 'jobs', label: 'Jobs', match: (type) => type === 'job_sync' || type === 'lead_to_job' },
        { key: 'workflows', label: 'Workflows', match: (type) => /trigger|action/.test(type || '') },
        { key: 'checks', label: 'Automatic checks', match: (type) => type === 'webhook_monitor' },
      ];
      const counts = groups.map((group) => ({
        ...group,
        count: this.weekItems.filter((item) => group.match(item.syncType)).length,
      }));
      const max = Math.max(1, ...counts.map((item) => item.count));
      return counts.map((item) => ({
        key: item.key,
        label: item.label,
        count: item.count,
        pct: Math.round((item.count / max) * 100),
      }));
    },
  },
  async mounted() {
    await this.refreshAll();
  },
  methods: {
    goTo(tab, subTab) {
      if (tab === 'Integration' && subTab === 'Manual Sync') {
        this.$emit('sync-contacts');
      }
      this.$emit('navigate', { tab, subTab: subTab || null });
    },
    async refreshAll() {
      this.loading = true;
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        const [statusRes, webhookRes, historyRes, contactsRes, jobsRes] = await Promise.all([
          fetch('/servicem8/status', { headers }).catch(() => null),
          fetch('/servicem8/webhooks/status', { headers }).catch(() => null),
          fetch('/servicem8/sync/history?limit=200', { headers }).catch(() => null),
          fetch('/servicem8/sync/contact-mappings', { headers }).catch(() => null),
          fetch('/servicem8/sync/job-mappings', { headers }).catch(() => null),
        ]);

        if (statusRes?.ok) {
          const data = await statusRes.json();
          this.connection = {
            connected: !!data.connected,
            accountValid: data.accountValid !== false,
            accountName: data.accountInfo?.name || data.currentAccountInfo?.name || '',
            validationError: data.validationError || '',
          };
        }

        if (webhookRes?.ok) {
          this.webhookStatus = await webhookRes.json();
        }

        if (historyRes?.ok) {
          const rows = await historyRes.json();
          this.recentHistory = Array.isArray(rows) ? rows : [];
        }

        if (contactsRes?.ok) {
          const rows = await contactsRes.json();
          this.mappedContacts = Array.isArray(rows) ? rows.length : 0;
        }

        if (jobsRes?.ok) {
          const rows = await jobsRes.json();
          this.mappedJobs = Array.isArray(rows) ? rows.length : 0;
        }

        this.lastUpdated = new Date();
        await this.loadApiKeys();
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadApiKeys() {
      this.loadingKeys = true;
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/api/test-keys', {
          method: 'GET',
          headers: { 'x-sso-token': ssoToken },
        });
        if (response.ok) {
          this.apiKeys = await response.json();
        } else {
          this.apiKeys = null;
        }
      } catch (error) {
        console.error('Error loading API keys:', error);
        this.apiKeys = null;
      } finally {
        this.loadingKeys = false;
      }
    },
    async copyToClipboard(text, key) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      this.copiedKey = key;
      window.setTimeout(() => {
        if (this.copiedKey === key) this.copiedKey = '';
      }, 1600);
    },
    friendlyType(value) {
      const labels = {
        trigger_registered: 'Workflow connected',
        trigger_deleted: 'Workflow removed',
        trigger_register: 'Workflow setup',
        trigger_received: 'Workflow update received',
        trigger_executed: 'Workflow ran',
        action_started: 'Action started',
        action_executed: 'Action finished',
        webhook_monitor: 'Automatic check',
        contact_sync: 'Contact sync',
        job_sync: 'Job sync',
        lead_to_job: 'Lead to job',
        workflow_execution: 'Job workflow',
      };
      return labels[value] || String(value || 'Activity').replace(/_/g, ' ');
    },
    friendlyDirection(value) {
      if (value === 'ghl_to_sm8') return `${this.brand.agencyName} → ServiceM8`;
      if (value === 'sm8_to_ghl') return `ServiceM8 → ${this.brand.agencyName}`;
      return 'Field Flow';
    },
    relativeTime(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      const diff = Date.now() - date.getTime();
      const minutes = Math.round(diff / 60000);
      if (minutes < 1) return 'just now';
      if (minutes < 60) return `${minutes} min ago`;
      const hours = Math.round(minutes / 60);
      if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      const days = Math.round(hours / 24);
      if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
        window.addEventListener('message', ({ data }) => {
          if (data.message === 'REQUEST_USER_DATA_RESPONSE') {
            resolve(data.payload);
          }
        });
      });
    },
  },
  emits: ['sync-contacts', 'sync-lead-to-job', 'view-history', 'navigate'],
};
</script>

<style scoped>
.dash {
  padding: 8px 0 32px;
  color: #0f172a;
}

.dash-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 22px;
}

.eyebrow {
  margin: 0 0 4px;
  color: #24acfe;
  font-size: 13px;
  font-weight: 600;
}

.dash-hero h1 {
  margin: 0;
  font-size: 28px;
  letter-spacing: -0.03em;
}

.hero-sub,
.updated,
.kpi-meta,
.card-head p,
.empty,
.activity-note,
.account-dl dt,
.details-grid dt,
.setup-list p,
.key-row p,
.key-note {
  color: #64748b;
}

.hero-sub {
  margin: 6px 0 0;
  font-size: 14px;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.updated {
  font-size: 12px;
}

.kpi-grid,
.dash-columns,
.action-grid {
  display: grid;
  gap: 16px;
}

.kpi-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 16px;
}

.dash-columns {
  grid-template-columns: minmax(0, 1.6fr) minmax(280px, 0.9fr);
  align-items: start;
}

.card,
.kpi {
  background: #fff;
  border: 1px solid #e6eef6;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.kpi {
  display: flex;
  gap: 12px;
  padding: 18px;
}

.kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef7ff;
  color: #24acfe;
  flex-shrink: 0;
}

.kpi.tone-ok .kpi-icon {
  background: #ecfdf5;
  color: #059669;
}

.kpi.tone-warn .kpi-icon {
  background: #fff7ed;
  color: #d97706;
}

.kpi.tone-bad .kpi-icon {
  background: #fef2f2;
  color: #dc2626;
}

.kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.kpi-meta {
  margin-top: 4px;
  font-size: 12px;
}

.card {
  padding: 18px 20px 20px;
}

.card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.card-head h2,
.setup-list strong,
.activity-main strong,
.account-dl dd,
.details-grid dd,
.key-row strong {
  margin: 0;
  color: #0f172a;
}

.card-head h2 {
  font-size: 17px;
}

.card-head p {
  margin: 4px 0 0;
  font-size: 13px;
}

.ghost-btn,
.text-btn,
.action-tile {
  border: 1px solid #d9e6f2;
  background: #fff;
  color: #0f172a;
  cursor: pointer;
}

.ghost-btn,
.text-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  font-weight: 600;
}

.ghost-btn {
  padding: 8px 12px;
  font-size: 13px;
}

.ghost-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.text-btn {
  border: none;
  background: transparent;
  color: #24acfe;
  padding: 0;
  font-size: 13px;
  white-space: nowrap;
}

.spinning {
  animation: dash-spin 0.8s linear infinite;
}

@keyframes dash-spin {
  to { transform: rotate(360deg); }
}

.setup-list,
.activity-feed,
.bars,
.account-dl {
  list-style: none;
  margin: 0;
  padding: 0;
}

.setup-list li,
.activity-feed li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.setup-list li {
  padding: 12px 0;
  border-top: 1px solid #eef2f6;
}

.setup-list li:first-child {
  border-top: none;
  padding-top: 0;
}

.setup-mark {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: #eef7ff;
  color: #24acfe;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.setup-list li.done .setup-mark {
  background: #ecfdf5;
  color: #059669;
}

.setup-list p {
  margin: 2px 0 0;
  font-size: 13px;
}

.activity-feed li {
  padding: 12px 0;
  border-top: 1px solid #eef2f6;
}

.activity-feed li:first-child {
  border-top: none;
  padding-top: 0;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 7px;
  flex-shrink: 0;
}

.dot.ok,
.pill.ok {
  background: #059669;
}

.dot.bad,
.pill.bad {
  background: #dc2626;
}

.activity-main {
  min-width: 0;
  flex: 1;
}

.activity-main strong,
.activity-note {
  display: block;
}

.activity-note {
  margin-top: 2px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.pill {
  color: #fff;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
}

.activity-side time {
  font-size: 11px;
  color: #94a3b8;
}

.side-stack {
  display: grid;
  gap: 16px;
}

.action-grid {
  grid-template-columns: 1fr 1fr;
}

.action-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 14px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  color: #0f172a;
}

.action-tile:hover,
.ghost-btn:hover:not(:disabled),
.text-btn:hover {
  border-color: #b6d4fe;
  background: #f8fbff;
}

.bars li + li {
  margin-top: 12px;
}

.bar-label {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #475569;
  margin-bottom: 6px;
}

.bar-track {
  height: 8px;
  border-radius: 999px;
  background: #eef2f6;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  min-width: 0;
}

.account-dl {
  display: grid;
  gap: 12px;
}

.account-dl dt {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.account-dl dd {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 600;
}

.empty {
  padding: 24px 8px;
  text-align: center;
  font-size: 14px;
}

.details-card,
.keys-card {
  margin-top: 16px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin: 0;
}

.details-grid dt,
.key-note {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.details-grid dd {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
}

.mono,
.key-row code,
.key-note code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 500;
}

.keys-list {
  display: grid;
  gap: 12px;
}

.key-row {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 12px;
  border: 1px solid #eef2f6;
  border-radius: 12px;
  background: #f8fbff;
}

.key-row p {
  margin: 4px 0 0;
  font-size: 12px;
}

.key-row code {
  display: block;
  padding: 10px 12px;
  border-radius: 8px;
  background: #0f172a;
  color: #e2e8f0;
  word-break: break-all;
}

.key-note {
  margin: 4px 0 0;
  text-transform: none;
  letter-spacing: 0;
  font-size: 13px;
}

.key-note code {
  background: #eef2f6;
  padding: 2px 6px;
  border-radius: 4px;
}

@media (max-width: 980px) {
  .kpi-grid,
  .dash-columns {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 720px) {
  .dash-hero,
  .card-head,
  .setup-list li,
  .key-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .kpi-grid,
  .dash-columns,
  .action-grid,
  .key-row {
    grid-template-columns: 1fr;
  }
}
</style>
